import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { MatchupWrapper, GenericWrapper, PlayerMatchupLayerWrapper, GameOuterWrapper, ContentWrapper } from './MiniScoreboardGame.style';
import ClickthroughWrapper from 'bdata-sns-shared/mini/components/ClickthroughWrapper';
import GameWrapper from './GameWrapper';
import ActionLayer from './ActionLayer/ActionLayer';
import StatusLayer from './StatusLayer/StatusLayer';
import TeamMatchupLayer from './TeamMatchupLayer/TeamMatchupLayer';
import GameDataLayer from './GameDataLayer/GameDataLayer';
import HoverToggle, { HoverToggleResources } from '@mlb/react-patterns/v4/components/HoverToggle/hoverToggle';
import GameStateTypes from 'bdata-sns-shared/game/types/GameStateTypes';
import getCondensedGamedayLink from 'bdata-sns-shared/utils/getGamedayUrls';
import retrieveButtonConfig, { cardTypes } from 'bdata-sns-shared/utils/productButtonConfigs';
import getTicketsLink from 'bdata-sns-shared/utils/getTicketsLink';
import { generateProductButtons } from 'bdata-sns-shared/utils/getProductButtons';
import PlayerMatchupLayer from 'bdata-sns-shared/game/components/PlayerMatchupLayer/PlayerMatchupLayer';
import MiniSBGameModes from 'bdata-sns-shared/mini/types/MiniSBGameModes';
import MiniScoreboardRoundHeader from 'bdata-react-mini-scoreboard-refresh/lib/components/MiniScoreboardRoundHeader/MiniScoreboardRoundHeader';
import VerticalGame from './VerticalGame';
import WhereToWatchContainer from 'bdata-sns-shared/game/containers/WhereToWatchContainer';
import getUseWhereToWatch from 'bdata-sns-shared/store/selectors/getUseWhereToWatch';
import getUrlRoot, { getSanitizedRoot } from 'bdata-sns-shared/utils/getUrlRoot';

const HoverTogglePropsTypes = {
    width: PropTypes.string,
    height: PropTypes.string,
    transitionSpeed: PropTypes.number,
    hoverContent: PropTypes.ReactElement,
    initTransitionStyles: PropTypes.string,
    hoverTransitionStyles: PropTypes.string,
    classNameOverride: PropTypes.bool
};

const TRANSITIONSPEED = 0.2;
const MiniScoreboardGame = ({
    game,
    isMedium,
    isMobile,
    dictionary = {},
    loading,
    tz,
    urlRoot,
    mediaEpg,
    isPlayoff,
    isPlayoffRefresh,
    assetsBaseUrl,
    clickHandler,
    className,
    showStatusLayer = true,
    customTeamLogoSize = 18,
    customTeamNameFontSize = 14,
    showTeamRecordsInPreview = true,
    showTeamRecordsInFinal = true,
    showSplitSquad = false,
    mode = MiniSBGameModes.INFINITY,
    basepathsWidth = 23,
    outsWidth,
    HoverToggleTypes = {
        default: {
            transitionSpeed: TRANSITIONSPEED,
            initTransitionStyles: 'border-radius: 6px;'
        },
        playerMatchUp: {
            transitionSpeed: TRANSITIONSPEED,
            initTransitionStyles: 'display: none;',
            classNameOverride: true,
            hoverTransitionStyles: 'display: flex; height: 65px; width: calc(100% - 16px); position: absolute; bottom: 0; left: 0; transition: 0.2s; border-radius: 0; border-top: solid 1px #F3F3F3; margin: 0 8px;'
        }
    },
    customProductButtons,
    locale,
    postseasonTheme = {},
    hoverToggleStyleOverride,
    teamLogoType,
    dataAttr = {},
    setActiveGame,
    isActiveGame,
    env
}) => {
    const [fetchWhereToWatch, setFetchWhereToWatch] = useState(false);
    const useWhereToWatch = useSelector(getUseWhereToWatch);

    const {
        status = {},
        teams,
        flags = {},
        linescore = {},
        tickets,
        content,
        isYouTubeFGOW,
        isAppleTv,
        gamePk,
        gamedayType,
        gameDate,
        officialDate,
        gameNumber,
        decisions,
        gameType,
        seriesStatus = {},
        parsedBroadcasts,
        nationalBroadcaster,
        ifNecessary,
        epglist,
        epgContentList,
        rescheduledFrom,
        resumedFrom,
        broadcasts,
        story: {
            link: storyLink
        } = {}
    } = game;

    const {
        abstractGameState,
        statusCode,
        codedGameState,
        abstractGameCode,
        abstract = {}
    } = status;
    const { isLoading } = loading;
    const {
        isPostponed,
        isSuspendedResumptionOnDate,
        isCancelled,
        isFreeGame,
        isWildCard,
        isInGameDelay,
        isSuspended,
        isInstantReplay,
        isUmpireReview,
        isManagerChallenge,
        isGameOver,
        isPostSeasonReady
    } = abstract;

    const isGameday = mode === MiniSBGameModes.GAMEDAY;
    const isVerticalGame = mode === MiniSBGameModes.VERTICAL;
    const showPostSeasonInfo = isPostSeasonReady && !isGameday;
    const gameState = abstractGameState || GameStateTypes.PREVIEW;
    const isInterrupted = isSuspended || isInGameDelay || isInstantReplay || isUmpireReview || isManagerChallenge;
    const notInterruptedPostseasonGame = (isPlayoff && !isPostponed && !isCancelled);
    const enabledInningState = isActiveGame || (gameState === 'Live' && !isInterrupted);
    const showPlayerMatchups = (notInterruptedPostseasonGame && (gameType === 'L' || gameType === 'W'));
    const useMobileFeatures = (mode === MiniSBGameModes.INFINITY) || (isGameday);

    // need to check for action layer buttons here because if there are no buttons
    // then we cannot use the hover toggle. Cannot tell whether the component is null
    // or not programmatically so need to check for hasButtons here
    const cardType = cardTypes.MINI_SB_INFINITY;
    const buttonsArr = retrieveButtonConfig({
        isMobile,
        isYouTubeFGOW,
        isAppleTv,
        cardType,
        gameState,
        customProductButtons,
        epglist,
        epgContentList
    });

    const ticketsLink = getTicketsLink(tickets);
    const homeTeam = teams && teams.home;
    const awayTeam = teams && teams.away;
    // generate formatted game time
    const awayTeamAbbrev = (awayTeam && awayTeam.team && awayTeam.team.abbreviation) || '';
    const homeTeamAbbrev = (homeTeam && homeTeam.team && homeTeam.team.abbreviation) || '';
    const awayTeamShortName = (awayTeam && awayTeam.team && awayTeam.team.shortName) || '';
    const homeTeamShortName = (homeTeam && homeTeam.team && homeTeam.team.shortName) || '';
    const homeTeamName = (homeTeam && homeTeam.team && homeTeam.team.teamName) || '';
    const awayTeamName = (awayTeam && awayTeam.team && awayTeam.team.teamName) || '';
    const handleWatchLink = useCallback((event) => {
        event.preventDefault();
        setFetchWhereToWatch(true);
    }, [setFetchWhereToWatch]);

    const watchHandlers = useMemo(() => ({
        watch: {
            onClick: handleWatchLink
        }
    }), [handleWatchLink]);

    // Builds the buttons using the button array fetched
    const { buttons, hasButtons } = generateProductButtons({
        cardType,
        isMobile,
        buttonsArr,
        dictionary,
        gamePk,
        urlRoot,
        ticketsLink,
        ticketLinkClassName: 'bdata-react-mini-sb-tickets-link',
        content,
        isLoading,
        mediaEpg,
        statusCode,
        abstractGameCode,
        isPostponed,
        gamedayType,
        isCancelled,
        gameDate,
        epglist,
        epgContentList,
        homeTeamName,
        awayTeamName,
        storyLink,
        broadcasts,
        handlersMap: useWhereToWatch === true ? watchHandlers : undefined,
        locale
    });

    // Set up either the clickthrough link (for mobile)...
    // or the hover-toggling (on desktop)
    // Retrieves the button array to use
    const UtilWrapper = (isMobile || isVerticalGame)
        ? ClickthroughWrapper
        : hasButtons
            ? HoverToggle
            : GenericWrapper;

    const ActionLayerbaseProps = {
        hasButtons,
        buttons
    };

    let hoverToggleDefault;
    if (!showPlayerMatchups) {
        hoverToggleDefault = {
            hoverContent: (
                <ActionLayer {...ActionLayerbaseProps} />
            )
        };
    }

    const HoverToggleProps = hoverToggleStyleOverride || ((!showPlayerMatchups && hoverToggleDefault) ? { ...HoverToggleTypes.default, ...hoverToggleDefault } : HoverToggleTypes.playerMatchup);
    const showActionLayer = showPlayerMatchups || (isPlayoff && !isPlayoffRefresh);

    let utilProps = {};

    if (!isGameday && !isVerticalGame) {
        if (!isMobile) {
            utilProps = HoverToggleProps;
        } else {
            utilProps = {
                href: getSanitizedRoot(
                    getCondensedGamedayLink(
                        urlRoot || getUrlRoot(locale, true),
                        gamedayType,
                        codedGameState,
                        abstractGameCode,
                        statusCode,
                        gamePk,
                        ''
                    )
                )
            };
        }
    }

    if (isVerticalGame) {
        utilProps.as = 'button';
    }
    const useStackedLayout = (gameState === GameStateTypes.FINAL && notInterruptedPostseasonGame);
    const gameNumberLabel = (isPlayoff && seriesStatus) ? seriesStatus.gameNumber : gameNumber;
    const classNames = `${className}${useMobileFeatures ? ' trk-gameday' : ''}`;
    const [hoverClassName, setClassName] = useState('');
    const leagueID = homeTeam?.team?.league?.id;

    const disableCell = (isGameday || isVerticalGame) && ((isCancelled && !rescheduledFrom) || (isPostponed && !resumedFrom));

    const handlers = useMemo(() => {
        if (disableCell) {
            return {};
        }
        return {
            onMouseLeave: () => {
                setClassName('unhovered');
            },
            onMouseEnter: () => {
                setClassName('hovered');
            }
        };
    }, [disableCell]);

    const hideGameNumber = isWildCard && seriesStatus.totalGames === 1;

    const onClickHandler = useCallback((e) => {
        if (!isLoading) {
            if (clickHandler) {
                clickHandler(gamePk, officialDate, e);
            }

            if (setActiveGame) {
                setActiveGame(gamePk);
            }
        }
    }, [isLoading, gamePk, clickHandler, setActiveGame, officialDate]);
    const trackingAttr = { 'data-tracking': `${awayTeam?.team?.abbreviation} At ${homeTeam?.team?.abbreviation}` };
    return (
        <GenericWrapper onClick={!disableCell && onClickHandler} {...dataAttr} {...trackingAttr} >
            <GameOuterWrapper
                className={`${hoverClassName} ${mode}`}
                {...handlers}
            >
                <GameWrapper mode={ mode } className={ classNames }>
                    <UtilWrapper
                        {...utilProps}
                    >
                        { !isVerticalGame
                            ? <ContentWrapper
                                disableCell={disableCell}
                                className={`content-wrapper ${isActiveGame ? 'selected' : ''} ${showPostSeasonInfo ? 'has-ps-game' : ''}`}
                            >
                                {showStatusLayer && (
                                    <>
                                        {useWhereToWatch === true && (
                                            <WhereToWatchContainer
                                                urlRoot={urlRoot}
                                                gamePk={gamePk}
                                                gameDate={gameDate}
                                                dictionary={dictionary}
                                                cardType={cardTypes.MINI_SB}
                                                fetchData={fetchWhereToWatch}
                                                setFetchData={setFetchWhereToWatch}
                                                env={env}
                                                locale={locale}
                                            />
                                        )}
                                        <StatusLayer
                                            flags={ flags }
                                            dictionary={ dictionary }
                                            mode={ mode }
                                            linescore={ linescore }
                                            gameDate={ gameDate }
                                            gameNumber={ gameNumberLabel }
                                            tz={ tz }
                                            gameState={ gameState }
                                            status ={ status }
                                            isPlayoff={ isPlayoff }
                                            isSuspendedResumptionOnDate={ isSuspendedResumptionOnDate }
                                            assetsBaseUrl={ assetsBaseUrl }
                                            broadcasts={ parsedBroadcasts }
                                            nationalBroadcaster={ nationalBroadcaster }
                                            isLoading={ isLoading }
                                            isInGameDelay={ isInGameDelay }
                                            isSuspended={ isSuspended }
                                            epglist={ epglist }
                                            epgContentList={ epgContentList }
                                            awayTeam={ awayTeam }
                                            homeTeam={ homeTeam }
                                            locale= {locale}
                                        />
                                    </>
                                )}
                                <MatchupWrapper
                                    extraPadding={ !(isFreeGame || isYouTubeFGOW || isAppleTv) }
                                    alwaysMobilePadding={ isGameday }
                                    $isPostseason = {showPostSeasonInfo}
                                >
                                    <TeamMatchupLayer
                                        teams={ teams }
                                        isMedium={ isMedium }
                                        mode={ mode }
                                        isLoading={ isLoading }
                                        gameState={ gameState }
                                        linescore={ linescore }
                                        customLogoSize={ customTeamLogoSize }
                                        customNameFontSize= { customTeamNameFontSize }
                                        showTeamRecordsInPreview={ showTeamRecordsInPreview }
                                        showSplitSquad={ showSplitSquad }
                                        teamLogoType={ teamLogoType }
                                        showTeamRecordsInFinal={ showTeamRecordsInFinal }
                                        isGameOver={ isGameOver }
                                        isActiveGame={ isActiveGame }
                                    />

                                    { enabledInningState && <GameDataLayer
                                        dictionary={ dictionary }
                                        linescore={ linescore }
                                        basepathsWidth={ basepathsWidth }
                                        outsWidth= { outsWidth }
                                        mode={mode}
                                        env={env}
                                        isActiveGame={ isActiveGame }
                                        isLoading={ isLoading }
                                    />
                                    }
                                </MatchupWrapper>

                                {showPlayerMatchups && (
                                    <PlayerMatchupLayerWrapper>
                                        <PlayerMatchupLayer
                                            homeTeam={ homeTeam }
                                            awayTeam={ awayTeam }
                                            linescore={ linescore }
                                            decisions={ decisions }
                                            gameState={ gameState }
                                            abstract={ abstract }
                                            dictionary={ dictionary }
                                            isLoading={ isLoading }
                                            awayTeamAbbrev={ awayTeamAbbrev }
                                            homeTeamAbbrev={ homeTeamAbbrev }
                                            awayTeamShortName={ awayTeamShortName }
                                            homeTeamShortName={ homeTeamShortName }
                                            urlRoot={ urlRoot }
                                            showHeadShot={ false }
                                            playerStatsInline={ useStackedLayout }
                                            useStackedLayout={ useStackedLayout }
                                            mode={ cardTypes.MINI_SB }
                                        />
                                    </PlayerMatchupLayerWrapper>
                                )}

                                {showActionLayer && <ActionLayer className={ HoverToggleResources.CLASSNAME } {...ActionLayerbaseProps} /> }
                                { showPostSeasonInfo && <MiniScoreboardRoundHeader
                                    seriesStatus={ !(isPostponed || isCancelled) ? seriesStatus : undefined }
                                    dictionary={ dictionary }
                                    hideGameNumber={ hideGameNumber }
                                    isLoading={ isLoading }
                                    assetsBaseUrl={ assetsBaseUrl }
                                    gameState={ gameState }
                                    gameType={ gameType }
                                    leagueID= { leagueID }
                                    ifNecessary= { ifNecessary }
                                />}
                            </ContentWrapper>
                            : <VerticalGame
                                disableCell={disableCell}
                                flags={ flags }
                                dictionary={ dictionary }
                                mode={ mode }
                                linescore={ linescore }
                                gameDate={ gameDate }
                                gameNumber={ gameNumberLabel }
                                status={ status }
                                teams={teams}
                                isLoading={isLoading}
                                basepathsWidth={ basepathsWidth }
                                outsWidth= { outsWidth }
                                isActiveGame={ isActiveGame }
                                gameState={ gameState }
                                tz={ tz }
                                isPlayoff={ isPlayoff }
                                isSuspendedResumptionOnDate={ isSuspendedResumptionOnDate }
                                assetsBaseUrl={ assetsBaseUrl }
                                isInGameDelay={ isInGameDelay }
                                isSuspended={ isSuspended }
                                enabledInningState={gameState === 'Live' && !isInterrupted}
                                env={env}
                            /> }
                    </UtilWrapper>
                </GameWrapper>
            </GameOuterWrapper>
        </GenericWrapper>
    );
};

MiniScoreboardGame.propTypes = {
    game: PropTypes.object,
    isMedium: PropTypes.bool,
    dictionary: PropTypes.object,
    loading: PropTypes.object,
    urlRoot: PropTypes.string,
    tz: PropTypes.string,
    mediaEpg: PropTypes.object,
    isPlayoff: PropTypes.bool,
    assetsBaseUrl: PropTypes.string,
    showStatusLayer: PropTypes.bool,
    showTeamRecordsInPreview: PropTypes.bool,
    showTeamRecordsInFinal: PropTypes.bool,
    customTeamLogoSize: PropTypes.number,
    customTeamNameFontSize: PropTypes.number,
    showSplitSquad: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(MiniSBGameModes)),
    HoverToggleTypes: PropTypes.shape({
        default: PropTypes.shape(HoverTogglePropsTypes),
        playerMatchup: PropTypes.shape(HoverTogglePropsTypes)
    }),
    hideStorylines: PropTypes.bool,
    isMobile: PropTypes.bool,
    basepathsWidth: PropTypes.number,
    outsWidth: PropTypes.number,
    className: PropTypes.string,
    customProductButtons: PropTypes.object,
    isPlayoffRefresh: PropTypes.bool,
    locale: PropTypes.string,
    hoverToggleStyleOverride: PropTypes.object,
    setActiveGame: PropTypes.func,
    activeGame: PropTypes.number,
    clickHandler: PropTypes.func,
    postseasonTheme: PropTypes.object,
    teamLogoType: PropTypes.string,
    dataAttr: PropTypes.object,
    isActiveGame: PropTypes.bool,
    env: PropTypes.string
};

export default MiniScoreboardGame;
